import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Wifi, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const STATUS_STYLE = {
  CONNECTED: 'bg-emerald-600 text-white', EMPTY: 'bg-amber-500 text-white',
  ERROR: 'bg-red-600 text-white', PENDING_CREDENTIALS: 'bg-amber-500 text-white',
  NO_ADAPTER: 'bg-muted text-muted-foreground',
};

export default function AdminEsim() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.adminEsimProviders().then(({ data }) => setData(data)).catch(() => toast.error('Failed to load eSIM providers'));
  useEffect(() => { document.title = 'eSIM Providers | Admin'; load(); }, []);

  const toggle = async (id, enabled) => { try { await api.adminEsimToggleProvider(id, enabled); toast.success(`${id} ${enabled ? 'enabled' : 'disabled'}`); load(); } catch { toast.error('Update failed'); } };
  const setPriority = async (id, priority) => { try { await api.adminEsimUpdateProvider(id, { priority: Number(priority) }); load(); } catch { toast.error('Update failed'); } };
  const setComparison = async (id, in_comparison) => { try { await api.adminEsimUpdateProvider(id, { in_comparison }); load(); } catch { toast.error('Update failed'); } };
  const sync = async () => { setSyncing(true); try { const { data } = await api.adminEsimSync(); toast.success(`Synced ${data.run.total_products} plans`); load(); } catch { toast.error('Sync failed'); } finally { setSyncing(false); } };

  if (!data) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="admin-esim">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Wifi className="h-5 w-5 text-accent" /> eSIM Providers</h1>
          <p className="text-sm text-muted-foreground">Multi-provider marketplace · {data.catalogue_size} live plans in catalogue</p>
        </div>
        <Button onClick={sync} disabled={syncing} className="rounded-xl bg-accent text-accent-foreground" data-testid="admin-esim-sync">
          {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Sync now
        </Button>
      </div>

      {data.last_sync && (
        <Card className="p-3 rounded-xl text-xs text-muted-foreground flex items-center gap-2" data-testid="admin-esim-last-sync">
          {data.last_sync.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
          Last sync {new Date(data.last_sync.finished_at).toLocaleString()} · {data.last_sync.total_products} plans
          {(data.last_sync.providers || []).map((pr) => <span key={pr.provider} className="ml-2">{pr.provider}: {pr.ok ? `${pr.count}` : `error`}</span>)}
        </Card>
      )}

      <div className="space-y-3">
        {data.providers.map((p) => (
          <Card key={p.provider_id} className="p-4 rounded-2xl" data-testid={`admin-esim-provider-${p.provider_id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {p.logo && <img src={p.logo} alt="" className="h-5" onError={(e) => { e.target.style.display = 'none'; }} />}
                <div>
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.provider_id} · required: {(p.required_env || []).join(', ') || 'none'}</p>
                </div>
                <Badge className={`rounded-full text-[10px] ${STATUS_STYLE[p.health?.status] || 'bg-muted'}`}>{p.health?.status}{p.health?.count != null ? ` · ${p.health.count}` : ''}</Badge>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">Enabled</span><Switch checked={!!p.enabled} onCheckedChange={(v) => toggle(p.provider_id, v)} data-testid={`admin-esim-toggle-${p.provider_id}`} /></label>
                <label className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">Compare</span><Switch checked={!!p.in_comparison} onCheckedChange={(v) => setComparison(p.provider_id, v)} /></label>
                <label className="flex items-center gap-1.5 text-sm"><span className="text-muted-foreground">Priority</span><Input type="number" defaultValue={p.priority} onBlur={(e) => setPriority(p.provider_id, e.target.value)} className="w-16 h-8 rounded-lg" data-testid={`admin-esim-priority-${p.provider_id}`} /></label>
              </div>
            </div>
            {p.health?.error && <p className="text-xs text-red-600 mt-2">{p.health.error}</p>}
            {!p.has_adapter && <p className="text-xs text-amber-600 mt-2">No adapter registered — add one in providers/esim_registry.py</p>}
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Known adapters: {data.known_adapters.join(', ')}. Add Yesim/Klook by writing an adapter + registering it — no frontend/DB changes.</p>
    </div>
  );
}
